var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, } from "react";
import { Box, Grid, Typography, Alert, useMediaQuery } from "@mui/material";
import { useForm, useFieldArray, useWatch, } from "react-hook-form";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
// Components
import { FSwitch } from "@ui/components/inputs/FSwitch";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FMultiSelectChips } from "@ui/components/inputs/FMultiSelectChips";
import { FInput } from "@ui/components/inputs/FInput";
import { FPhoneInput } from "@ui/components/inputs/FPhoneInput";
import { FTimePicker } from "@ui/components/inputs/FTimePicker";
import { SButton } from "@ui/components/buttons/SButton";
import { SIconButton } from "@ui/components/buttons";
import { SectionWithInfo } from "@ui/components/section/SectionWithInfo";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { updateAccessPointConfiguration } from "@reducers/access-points/thunk/access-point-thunk";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { tooltipTextsApi, TOOLTIP_KEYS } from "@api/enpoints/tooltip-texts-api";
import { AlertType, RepeatAlarm, } from "@reducers/access-points/models/access-point-dto";
import { alertTypeToSelectValue, parseAlertTypeOrDefault, parseRepeatAlarmOrDefault, repeatAlarmToSelectValue, } from "@utility/alarm-enum-coercion";
import { alertReportTypesToBooleans, booleansToAlertReportTypes, } from "../utils/settings-transform";
import { jEnd, colCenter } from "@ui/components/style/style";
import { useAlarmRecipientCrossValidation } from "@ui/hooks/useAlarmRecipientCrossValidation";
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const defaultContact = () => ({
    email: "",
    number: "",
    alertReportTypes: [],
    startTime: null,
    endTime: null,
    timeZoneId: BROWSER_TIMEZONE,
});
// Helper to parse time string "HH:mm:ss" or "HH:mm" to Date
const parseTimeString = (timeStr) => {
    if (!timeStr)
        return null;
    try {
        const today = new Date();
        const [hours, minutes, seconds] = timeStr.split(":").map(Number);
        today.setHours(hours || 0, minutes || 0, seconds || 0, 0);
        return today;
    }
    catch (_a) {
        return null;
    }
};
// Helper to format Date to time string "HH:mm:ss"
const formatTimeToString = (date) => {
    if (!date)
        return undefined;
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
};
// Load contacts from AP alarm settings recipients (single source).
function loadContactsFromAp(ap) {
    var _a;
    const rec = (_a = ap.alarmSettings) === null || _a === void 0 ? void 0 : _a.recipients;
    if (rec && rec.length > 0) {
        return rec.map((r) => {
            var _a, _b, _c, _d, _e;
            return ({
                email: (_b = (_a = r.email) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "",
                number: ((_c = r.phone) !== null && _c !== void 0 ? _c : "").trim(),
                alertReportTypes: booleansToAlertReportTypes({
                    allowHighTemp: !!r.allowHighTemp,
                    allowLowTemp: !!r.allowLowTemp,
                    allowNoNode: !!r.allowNoNode,
                    allowNoAp: !!r.allowNoAp,
                    allowReporting: !!r.allowReporting,
                }),
                startTime: parseTimeString((_d = r.startTime) !== null && _d !== void 0 ? _d : undefined),
                endTime: parseTimeString((_e = r.endTime) !== null && _e !== void 0 ? _e : undefined),
                timeZoneId: r.timeZoneId || BROWSER_TIMEZONE,
            });
        });
    }
    return [defaultContact()];
}
function contactHasEmailOrPhone(c) {
    var _a, _b;
    const email = (_a = c.email) === null || _a === void 0 ? void 0 : _a.trim();
    const raw = (_b = c.number) === null || _b === void 0 ? void 0 : _b.trim();
    const phoneOk = !!raw && raw !== "+31 ";
    return !!(email || phoneOk);
}
// Transform contacts to alarmSettings.recipients (skip rows without email or phone).
function transformContactsForApi(contacts) {
    const filled = contacts.filter(contactHasEmailOrPhone);
    const recipients = filled.map((c) => {
        var _a;
        const flags = alertReportTypesToBooleans(c.alertReportTypes);
        const email = (_a = c.email) === null || _a === void 0 ? void 0 : _a.trim();
        return {
            email: email || null,
            phone: (() => {
                var _a;
                const phone = (_a = c.number) === null || _a === void 0 ? void 0 : _a.trim();
                if (!phone || phone === "+31 ")
                    return null;
                return phone.replace(/\s/g, "");
            })(),
            startTime: formatTimeToString(c.startTime),
            endTime: formatTimeToString(c.endTime),
            timeZoneId: c.timeZoneId || BROWSER_TIMEZONE,
            allowHighTemp: flags.allowHighTemp,
            allowLowTemp: flags.allowLowTemp,
            allowNoNode: flags.allowNoNode,
            allowNoAp: flags.allowNoAp,
            allowReporting: flags.allowReporting,
        };
    });
    return { recipients };
}
const AlarmSettings = forwardRef(({ control, errors, validationRules, isEdit, ap, onRecipientSaveBlockedChange }, ref) => {
    var _a;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["common"]);
    const alertingOptions = [
        { label: t("common:options.alerting.emailSms"), value: String(AlertType.EmailAndSMS) },
        { label: t("common:options.alerting.emailOnly"), value: String(AlertType.EmailOnly) },
        { label: t("common:options.alerting.smsOnly"), value: String(AlertType.SMSOnly) },
    ];
    const repeatAlarmOptions = [
        { label: t("common:options.repeatAlarm.repeatUntilResolved"), value: String(RepeatAlarm.AlarmRepeatUntilResolved) },
        { label: t("common:options.repeatAlarm.repeat3Times"), value: String(RepeatAlarm.AlaramRepeats3Times) },
        { label: t("common:options.repeatAlarm.noRepeat"), value: String(RepeatAlarm.NoRepeat) },
    ];
    const reportingIntervalOptions = [
        { label: t("common:options.reportingInterval.day"), value: "day" },
        { label: t("common:options.reportingInterval.week"), value: "week" },
        { label: t("common:options.reportingInterval.month"), value: "month" },
        { label: t("common:options.reportingInterval.quarter"), value: "quarter" },
    ];
    const reportingBlockOptions = [
        { label: t("common:options.reportingBlock.hours", { count: 2 }), value: "2" },
        { label: t("common:options.reportingBlock.hours", { count: 4 }), value: "4" },
        { label: t("common:options.reportingBlock.hours", { count: 6 }), value: "6" },
        { label: t("common:options.reportingBlock.hours", { count: 12 }), value: "12" },
        { label: t("common:options.reportingBlock.hours", { count: 24 }), value: "24" },
    ];
    const alertReportOptions = [
        { label: t("common:options.alertReport.lowTemperature"), value: "lowTemperature" },
        { label: t("common:options.alertReport.highTemperature"), value: "highTemperature" },
        { label: t("common:options.alertReport.noNodeCommunication"), value: "noNodeCommunication" },
        { label: t("common:options.alertReport.noApCommunication"), value: "noApCommunication" },
        { label: t("common:options.alertReport.reporting"), value: "reporting" },
    ];
    const { updateConfigLoading } = useAppSelector((state) => state.accessPoint);
    // Tooltip state for General section
    const [generalTooltip, setGeneralTooltip] = useState("");
    // Fetch saved tooltip texts from API on mount
    useEffect(() => {
        const fetchTooltips = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield tooltipTextsApi.getTooltipTexts();
                if (((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) && ((_b = res.data) === null || _b === void 0 ? void 0 : _b.data)) {
                    const d = res.data.data;
                    if (d[TOOLTIP_KEYS.alerting] != null)
                        setGeneralTooltip(d[TOOLTIP_KEYS.alerting]);
                }
            }
            catch (_c) {
                // Non-blocking; tooltips stay empty if fetch fails
            }
        });
        fetchTooltips();
    }, []);
    const handleTooltipSave = (key, value) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            const res = yield tooltipTextsApi.saveTooltipText({ key, text: value });
            if ((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) {
                toast.success(t("common:toasts.saved"));
            }
            else {
                toast.error(t("common:toasts.failedToSaveSettings"));
            }
        }
        catch (err) {
            toast.error(t("common:toasts.failedToSaveSettings"));
        }
    });
    const { control: alarmControl, handleSubmit, reset, formState: { errors: alarmErrors }, } = useForm({
        defaultValues: {
            blockAlarmEmails: false,
            alerting: String(AlertType.EmailAndSMS),
            repeatAlarm: String(RepeatAlarm.NoRepeat),
            reportingInterval: "day",
            reportingBlockHours: "24",
            contacts: [defaultContact()],
        },
    });
    const watchedContacts = useWatch({
        control: alarmControl,
        name: "contacts",
    });
    useAlarmRecipientCrossValidation({
        target: "accessPoint",
        accessPointUid: (_a = ap === null || ap === void 0 ? void 0 : ap.accessPointUID) !== null && _a !== void 0 ? _a : null,
        contacts: watchedContacts,
        onBlockedChange: onRecipientSaveBlockedChange,
    });
    // Expose submitForm method to parent via ref
    useImperativeHandle(ref, () => ({
        submitForm: () => {
            handleSubmit(handleSaveConfiguration)();
        },
    }));
    const { fields, append, remove } = useFieldArray({
        control: alarmControl,
        name: "contacts",
    });
    // Track which AP UID we last initialised from so that SignalR patches
    // (which swap the ap object reference without changing alarm settings)
    // do not wipe out unsaved contact edits.
    const initializedApUidRef = useRef(null);
    // Reset form when AP data is loaded for the first time (or for a new AP).
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (!ap)
            return;
        const uid = ap.accessPointUID;
        if (initializedApUidRef.current === uid)
            return;
        initializedApUidRef.current = uid;
        const loadedContacts = loadContactsFromAp(ap);
        reset({
            blockAlarmEmails: ((_a = ap.alarmSettings) === null || _a === void 0 ? void 0 : _a.blockAllAlaramsAndSMS) || false,
            alerting: alertTypeToSelectValue((_b = ap.alarmSettings) === null || _b === void 0 ? void 0 : _b.alertType),
            repeatAlarm: repeatAlarmToSelectValue((_c = ap.alarmSettings) === null || _c === void 0 ? void 0 : _c.alarmRepetition),
            reportingInterval: (((_d = ap.alarmSettings) === null || _d === void 0 ? void 0 : _d.reportingInterval) || "day").toLowerCase(),
            reportingBlockHours: ((_e = ap.alarmSettings) === null || _e === void 0 ? void 0 : _e.reportingBlockHours) != null
                ? String(ap.alarmSettings.reportingBlockHours)
                : "24",
            contacts: loadedContacts,
        });
    }, [ap, reset]);
    const addContact = () => {
        append(defaultContact());
    };
    const removeContact = (index) => {
        if (fields.length > 1) {
            remove(index);
        }
    };
    const handleAcknowledgeAlarms = () => {
        navigate("/alarms");
    };
    // Save configuration settings
    const handleSaveConfiguration = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(ap === null || ap === void 0 ? void 0 : ap.accessPointUID)) {
            toast.error(t("common:toasts.accessPointUidRequired"));
            return;
        }
        const { recipients } = transformContactsForApi(data.contacts);
        try {
            yield dispatch(updateAccessPointConfiguration({
                accessPointUid: ap.accessPointUID,
                data: {
                    accessPointUID: ap.accessPointUID,
                    alarmSettings: {
                        blockAllAlaramsAndSMS: data.blockAlarmEmails,
                        alertType: parseAlertTypeOrDefault(data.alerting),
                        alarmRepetition: parseRepeatAlarmOrDefault(data.repeatAlarm),
                        recipients,
                        reportingInterval: data.reportingInterval || "day",
                        reportingBlockHours: data.reportingBlockHours
                            ? parseInt(data.reportingBlockHours, 10)
                            : 24,
                    },
                    isGeneralSettings: false,
                    isAlarmSettings: true,
                },
            })).unwrap();
            toast.success(t("common:toasts.configurationUpdatedSuccessfully"));
        }
        catch (error) {
            toast.error(error || t("common:toasts.failedToUpdateConfiguration"));
        }
    });
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
    return (React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(Alert, { severity: "info", sx: {
                    backgroundColor: "#00B8D929",
                    border: "1px solid #00B8D929",
                    borderRadius: 1,
                    alignItems: "flex-start",
                    flexDirection: { xs: "column", sm: "row" },
                    "& .MuiAlert-icon": { color: "info.main" },
                    "& .MuiAlert-message": { flex: 1 },
                    "& .MuiAlert-action": {
                        alignItems: { xs: "flex-start", sm: "center" },
                        pt: 0,
                        pl: { xs: 0, sm: 1 },
                        ml: { xs: 0, sm: "auto" },
                        mt: { xs: 1.5, sm: 0 },
                        width: { xs: "100%", sm: "auto" },
                    },
                }, icon: isSmallScreen ? false : (React.createElement(Icon, { icon: "eva:alert-circle-fill", width: 24, height: 24 })), action: React.createElement(SButton, { variant: "contained", color: "info", size: isSmallScreen ? "small" : "medium", endIcon: React.createElement(Icon, { icon: "solar:arrow-right-up-linear" }), onClick: handleAcknowledgeAlarms, sx: { width: { xs: "100%", sm: "auto" } } }, t("common:actions.acknowledgeAlarms")) },
                React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                    isSmallScreen && (React.createElement(Icon, { color: "#00B8D9", width: 20, height: 20, icon: "eva:alert-circle-fill" })),
                    React.createElement(Typography, { variant: "subtitle2", color: "info.dark", sx: { mb: 0.5 } }, t("common:banners.alarmRepetitionAlertTitle"))),
                React.createElement(Typography, { variant: "body2", color: "text.secondary" }, t("common:banners.alarmRepetitionAlertBody1")),
                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 } }, t("common:banners.alarmRepetitionAlertBody2")))),
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(SectionWithInfo, { title: t("common:headings.general"), tooltipText: generalTooltip, onTooltipSave: (value) => {
                    setGeneralTooltip(value);
                    handleTooltipSave(TOOLTIP_KEYS.alerting, value);
                } },
                React.createElement(Box, { sx: { mb: 2 } },
                    React.createElement(FSwitch, { color: "error", name: "blockAlarmEmails", control: alarmControl, label: t("common:labels.blockAlarmEmails") })),
                React.createElement(Grid, { container: true, spacing: 2 },
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "alerting", control: alarmControl, label: t("common:labels.alerting"), options: alertingOptions })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "repeatAlarm", control: alarmControl, label: t("common:labels.repeatAlarm"), options: repeatAlarmOptions })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "reportingInterval", control: alarmControl, label: t("common:labels.reportingInterval"), options: reportingIntervalOptions })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "reportingBlockHours", control: alarmControl, label: t("common:labels.reportingBlock"), options: reportingBlockOptions }))))),
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(Box, { sx: {
                    bgcolor: (theme) => theme.palette.background.paper,
                    borderRadius: 4,
                    px: 2,
                    py: 1,
                } },
                React.createElement(Typography, { variant: "h5", sx: { color: "primary.darker", mb: 2 } }, t("common:headings.contactsList")),
                fields.map((field, index) => (React.createElement(Box, { key: field.id, mb: 1.5 },
                    React.createElement(Grid, { container: true, spacing: 2, alignItems: "center" },
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2.5 } },
                            React.createElement(FInput, { name: `contacts.${index}.email`, control: alarmControl, label: t("common:labels.emails"), placeholder: t("common:placeholders.emailAddress"), type: "email" })),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                            React.createElement(FPhoneInput, { name: `contacts.${index}.number`, control: alarmControl, label: t("common:labels.contactNumber", { number: index + 1 }) })),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2.5 } },
                            React.createElement(FMultiSelectChips, { name: `contacts.${index}.alertReportTypes`, control: alarmControl, label: t("common:labels.alertReport"), options: alertReportOptions })),
                        React.createElement(Grid, { size: { xs: 12, sm: 3, md: "grow" } },
                            React.createElement(FTimePicker, { name: `contacts.${index}.startTime`, control: alarmControl, label: t("common:labels.startTimeOptional") })),
                        React.createElement(Grid, { size: { xs: 9, sm: 3, md: "grow" } },
                            React.createElement(FTimePicker, { name: `contacts.${index}.endTime`, control: alarmControl, label: t("common:labels.endTimeOptional") })),
                        React.createElement(Grid, { sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                width: "50px",
                            } },
                            React.createElement(SIconButton, { title: t("common:actions.remove"), size: "large", sx: { borderRadius: "50px" }, colorVariant: "inherit", onClick: () => removeContact(index), disabled: fields.length === 1 },
                                React.createElement(Icon, { icon: "material-symbols:close-rounded" }))))))),
                React.createElement(Box, { sx: Object.assign(Object.assign({}, jEnd), { mt: 1 }) },
                    React.createElement(SButton, { variant: "soft", color: "inherit", startIcon: React.createElement(Icon, { icon: "eva:plus-outline", width: 22, height: 22 }), onClick: addContact }, t("common:actions.addContact")))))));
});
export default AlarmSettings;
